import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { getFirebaseApp } from '../lib/firebase';

const ENV_PREFIX = import.meta.env.PROD ? 'prod' : 'dev';

/**
 * Listens to Firestore `signals/{env}-bookings` for real-time booking changes.
 * When a change arrives, invalidates the React Query bookings cache so
 * the calendar (and any other booking consumers) re-fetch automatically.
 *
 * If Firebase is not configured, falls back to polling at the given interval.
 */
export function useBookingSignal(pollFallbackMs = 15_000) {
  const queryClient = useQueryClient();
  const usingFirestore = useRef(false);

  useEffect(() => {
    let unsubscribe: (() => void) | undefined;
    let pollTimer: ReturnType<typeof setInterval> | undefined;

    async function setup() {
      const app = getFirebaseApp();
      if (app) {
        try {
          const { getFirestore, doc, onSnapshot } = await import('firebase/firestore');
          const db = getFirestore(app);
          let isFirst = true;
          unsubscribe = onSnapshot(doc(db, 'signals', `${ENV_PREFIX}-bookings`), () => {
            // Skip the initial snapshot (current state), only react to changes
            if (isFirst) { isFirst = false; return; }
            queryClient.invalidateQueries({ queryKey: ['bookings'] });
          });
          usingFirestore.current = true;
          return;
        } catch (err) {
          console.warn('Firestore listener setup failed, falling back to polling:', err);
        }
      }

      // Fallback: poll
      usingFirestore.current = false;
      pollTimer = setInterval(() => {
        queryClient.invalidateQueries({ queryKey: ['bookings'] });
      }, pollFallbackMs);
    }

    setup();

    return () => {
      unsubscribe?.();
      if (pollTimer) clearInterval(pollTimer);
    };
  }, [queryClient, pollFallbackMs]);

  return usingFirestore;
}
